if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.activeIndex = 0;
        this.visibleMediaIds = [];
        this.currentGallery = null;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.panStartX = 0;
        this.panStartY = 0;
        this.startTranslateX = 0;
        this.startTranslateY = 0;
        this.translateX = 0;
        this.translateY = 0;
        this.scale = 1;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.gestureWasPinch = false;
        this.lastTapAt = 0;

        this.previousButton = this.querySelector('.product-media-modal__nav-button--prev');
        this.nextButton = this.querySelector('.product-media-modal__nav-button--next');
        this.currentCounter = this.querySelector('[data-modal-current]');
        this.totalCounter = this.querySelector('[data-modal-total]');
        this.content = this.querySelector('.product-media-modal__content');

        this.previousButton?.addEventListener('click', () => this.showRelativeMedia(-1, true));
        this.nextButton?.addEventListener('click', () => this.showRelativeMedia(1, true));
        this.addEventListener('keydown', (event) => this.onKeydown(event));

        this.content?.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
        this.content?.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
        this.content?.addEventListener('touchend', (event) => this.onTouchEnd(event), { passive: true });
        this.content?.addEventListener('touchcancel', () => this.finishTouchGesture(), { passive: true });
        this.content?.addEventListener('dblclick', (event) => {
          if (window.matchMedia('(hover: hover)').matches && event.target === this.zoomTarget) {
            this.toggleDoubleTapZoom();
          }
        });
      }

      hide() {
        const isVideoShortcut = this.classList.contains('product-media-modal--video-shortcut');
        if (!isVideoShortcut) this.syncGalleryToActiveMedia();
        if (isVideoShortcut) {
          this.querySelectorAll('video').forEach((video) => {
            video.pause();
            video.currentTime = 0;
          });
        }
        this.resetZoom();
        document.documentElement.classList.remove('inewer-product-modal-open');
        super.hide();
        this.classList.remove('product-media-modal--video-shortcut');
      }

      show(opener) {
        if (opener.hasAttribute('data-video-shortcut')) {
          this.classList.add('product-media-modal--video-shortcut');
        } else {
          this.classList.remove('product-media-modal--video-shortcut');
        }
        super.show(opener);
        document.documentElement.classList.add('inewer-product-modal-open');
        this.syncVisibleMediaFromGallery();
        this.showActiveMedia();
      }

      get allMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      get mediaItems() {
        if (!this.visibleMediaIds.length) return this.allMediaItems;

        const itemById = new Map(
          this.allMediaItems.map((item) => [item.getAttribute('data-media-id'), item])
        );

        return this.visibleMediaIds.map((mediaId) => itemById.get(mediaId)).filter(Boolean);
      }

      get zoomTarget() {
        return this.querySelector('.product-media-modal__content > img[data-media-id].active');
      }

      syncVisibleMediaFromGallery() {
        if (this.classList.contains('product-media-modal--video-shortcut')) {
          this.currentGallery = null;
          this.visibleMediaIds = [this.openedBy.getAttribute('data-media-id')];
          return;
        }
        this.currentGallery = this.openedBy?.closest('media-gallery') || null;
        if (!this.currentGallery) {
          this.visibleMediaIds = [];
          return;
        }

        this.visibleMediaIds = Array.from(
          this.currentGallery.querySelectorAll('.product__media-item[data-media-id]')
        )
          .map((slide) =>
            slide.querySelector('modal-opener [data-media-id]')?.getAttribute('data-media-id')
          )
          .filter((mediaId, index, mediaIds) => mediaId && mediaIds.indexOf(mediaId) === index);
      }

      syncGalleryToActiveMedia() {
        const activeMedia = this.mediaItems[this.activeIndex];
        const mediaId = activeMedia?.getAttribute('data-media-id');
        if (!this.currentGallery || !mediaId) return;

        const gallerySlide = Array.from(
          this.currentGallery.querySelectorAll('.product__media-item[data-media-id]')
        ).find(
          (slide) =>
            slide.querySelector('modal-opener [data-media-id]')?.getAttribute('data-media-id') === mediaId
        );

        if (!gallerySlide) return;
        this.currentGallery.setActiveMedia?.(gallerySlide.dataset.mediaId, false);

        const viewer = this.currentGallery.elements?.viewer;
        const currentCounter = viewer?.querySelector('.slider-counter--current');
        const totalCounter = viewer?.querySelector('.slider-counter--total');
        if (currentCounter) currentCounter.textContent = String(this.activeIndex + 1);
        if (totalCounter) totalCounter.textContent = String(this.mediaItems.length);
      }

      showActiveMedia() {
        const activeGalleryMediaId = this.currentGallery
          ?.querySelector('.product__media-item.is-active button[data-media-id]')
          ?.getAttribute('data-media-id');
        const mediaId = activeGalleryMediaId || this.openedBy?.getAttribute('data-media-id');
        const index = this.mediaItems.findIndex((item) => item.getAttribute('data-media-id') === mediaId);
        this.showMediaAt(index >= 0 ? index : 0);
      }

      showRelativeMedia(direction, allowWhileZoomed = false) {
        if (this.scale > 1.01 && !allowWhileZoomed) return;
        this.showMediaAt(this.activeIndex + direction);
      }

      showMediaAt(index) {
        const items = this.mediaItems;
        if (!items.length) return;

        this.resetZoom();
        const nextIndex = Math.max(0, Math.min(index, items.length - 1));
        this.allMediaItems.forEach((item) => {
          item.classList.remove('active');
          item.setAttribute('aria-hidden', 'true');
        });

        const activeMedia = items[nextIndex];
        activeMedia.classList.add('active');
        activeMedia.setAttribute('aria-hidden', 'false');
        this.activeIndex = nextIndex;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }

        if (
          this.classList.contains('product-media-modal--video-shortcut') &&
          activeMedia.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent?.querySelector('video')
        ) {
          activeMedia.loadContent(false);
          const video = activeMedia.querySelector('video');
          if (video) {
            video.muted = true;
            video.play()?.catch(() => {});
          }
        }

        if (this.content) {
          this.content.scrollLeft = 0;

          if (window.matchMedia('(min-width: 750px)').matches) {
            // Desktop/tablet modal media are a vertical scroll stack. The
            // active class alone does not move the scroll viewport, so align
            // the content viewport with the media opened by this exact Zoom
            // trigger instead of resetting to the first item.
            const alignActiveMedia = () => {
              const contentRect = this.content.getBoundingClientRect();
              const activeRect = activeMedia.getBoundingClientRect();
              this.content.scrollTop += activeRect.top - contentRect.top;
            };
            alignActiveMedia();
            window.requestAnimationFrame(alignActiveMedia);
          } else {
            this.content.scrollTop = 0;
          }
        }

        if (this.currentCounter) this.currentCounter.textContent = String(nextIndex + 1);
        if (this.totalCounter) this.totalCounter.textContent = String(items.length);

        const navigation = this.querySelector('.product-media-modal__navigation');
        if (navigation) navigation.hidden = items.length <= 1;
        this.updateNavigationState();
      }

      updateNavigationState() {
        if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
        if (this.nextButton) {
          this.nextButton.disabled = this.activeIndex === this.mediaItems.length - 1;
        }
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showRelativeMedia(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showRelativeMedia(1);
        } else if (event.key === 'Escape') {
          this.resetZoom();
        }
      }

      onTouchStart(event) {
        if (!window.matchMedia('(max-width: 749px)').matches || !this.zoomTarget) return;

        if (event.touches.length === 2) {
          this.gestureWasPinch = true;
          this.pinchStartDistance = this.touchDistance(event.touches);
          this.pinchStartScale = this.scale;
          return;
        }

        if (event.touches.length !== 1) return;
        const touch = event.touches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.panStartX = touch.clientX;
        this.panStartY = touch.clientY;
        this.startTranslateX = this.translateX;
        this.startTranslateY = this.translateY;
      }

      onTouchMove(event) {
        if (!window.matchMedia('(max-width: 749px)').matches || !this.zoomTarget) return;

        if (event.touches.length === 2) {
          event.preventDefault();
          this.gestureWasPinch = true;
          const distance = this.touchDistance(event.touches);
          const nextScale = this.pinchStartDistance
            ? this.pinchStartScale * (distance / this.pinchStartDistance)
            : this.scale;
          this.setZoom(nextScale, this.translateX, this.translateY);
          return;
        }

        if (event.touches.length === 1 && this.scale > 1.01) {
          event.preventDefault();
          const touch = event.touches[0];
          this.content?.classList.add('is-panning');
          this.setZoom(
            this.scale,
            this.startTranslateX + touch.clientX - this.panStartX,
            this.startTranslateY + touch.clientY - this.panStartY
          );
        }
      }

      onTouchEnd(event) {
        if (!window.matchMedia('(max-width: 749px)').matches || !this.zoomTarget) return;

        if (this.gestureWasPinch) {
          if (event.touches.length === 0) this.finishTouchGesture();
          return;
        }

        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        const isTap = Math.abs(deltaX) < 12 && Math.abs(deltaY) < 12;

        this.content?.classList.remove('is-panning');

        if (isTap) {
          const now = Date.now();
          if (now - this.lastTapAt < 320) {
            this.toggleDoubleTapZoom();
            this.lastTapAt = 0;
          } else {
            this.lastTapAt = now;
          }
          return;
        }

        if (this.scale > 1.01) return;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.showRelativeMedia(deltaX < 0 ? 1 : -1);
      }

      finishTouchGesture() {
        this.gestureWasPinch = false;
        this.pinchStartDistance = 0;
        this.content?.classList.remove('is-panning');
        if (this.scale < 1.05) this.resetZoom();
      }

      touchDistance(touches) {
        return Math.hypot(
          touches[1].clientX - touches[0].clientX,
          touches[1].clientY - touches[0].clientY
        );
      }

      toggleDoubleTapZoom() {
        if (!this.zoomTarget) return;
        if (this.scale > 1.01) this.resetZoom();
        else this.setZoom(2, 0, 0);
      }

      setZoom(scale, translateX, translateY) {
        const target = this.zoomTarget;
        if (!target) return;

        const nextScale = Math.max(1, Math.min(3, scale));
        if (nextScale <= 1.01) {
          this.resetZoom();
          return;
        }

        const maxX = (target.offsetWidth * (nextScale - 1)) / 2;
        const maxY = (target.offsetHeight * (nextScale - 1)) / 2;
        this.scale = nextScale;
        this.translateX = Math.max(-maxX, Math.min(maxX, translateX));
        this.translateY = Math.max(-maxY, Math.min(maxY, translateY));

        target.style.transform =
          `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;
        this.content?.classList.toggle('is-zoomed', this.scale > 1.01);
        this.updateNavigationState();
      }

      resetZoom() {
        const target = this.zoomTarget;
        if (target) target.style.transform = '';
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.pinchStartDistance = 0;
        this.content?.classList.remove('is-zoomed', 'is-panning');
        this.updateNavigationState();
      }
    }
  );
}



